import React, { useMemo, useState } from "react";
import { INSTALLMENT_PLANS_LIB } from "../../../../../constants/common";

const getSortedFloors = (data) =>
    [...(data?.floorRanges || [])].sort((a, b) => {
        const aFloor = parseInt(a.floorRange.split("-")[0], 10);
        const bFloor = parseInt(b.floorRange.split("-")[0], 10);
        return bFloor - aFloor;
    });

const formatPrice = (value) => {
    if (value == null || value === "") return "—";
    return `${String(value).toLocaleString?.() || value} ₽`;
};

const getCellTone = (value) => {
    if (value == null || value === "") return "text-[#98a2b3]";
    return "text-[#101828]";
};

const isSamePrice = (commonValue, fancyValue) => {
    if (commonValue == null || commonValue === "") return false;
    return String(commonValue) === String(fancyValue);
};

const getPeriodPriceSignature = (sortedFloors, installmentPlans, period) =>
    JSON.stringify(
        sortedFloors.map((floor) => {
            const priceMap = {};
            floor.prices
                .filter(
                    (price) =>
                        price.installmentPeriod === period || price.installmentPeriod === ""
                )
                .forEach((price) => {
                    priceMap[price.installmentPlan] = {
                        commonFlat: price?.commonFlat ?? null,
                        fancyViewFlat: price?.fancyViewFlat ?? null,
                    };
                });

            return {
                floorRange: floor.floorRange,
                plans: installmentPlans.map((plan) => ({
                    plan,
                    commonFlat: priceMap[plan]?.commonFlat ?? null,
                    fancyViewFlat: priceMap[plan]?.fancyViewFlat ?? null,
                })),
            };
        })
    );

const groupPeriodsBySignature = (periods, sortedFloors, installmentPlans) => {
    const groups = [];

    periods.forEach((period) => {
        const signature = getPeriodPriceSignature(
            sortedFloors,
            installmentPlans,
            period
        );
        const prevGroup = groups[groups.length - 1];

        if (prevGroup && prevGroup.signature === signature) {
            prevGroup.periods.push(period);
            return;
        }

        groups.push({
            signature,
            periods: [period],
        });
    });

    return groups;
};

const formatPeriodRange = (periods) => {
    if (!periods?.length) return "";
    if (periods.length === 1) return `${periods[0]} мес.`;
    return `${periods[0]} - ${periods[periods.length - 1]} мес.`;
};

const PriceCell = ({ value, className = "" }) => (
    <td
        className={`pricing-showcase_table-cell border-b border-[#eef2f6] px-4 py-3 text-right text-[14px] font-medium ${className} ${getCellTone(value)}`}
    >
        {formatPrice(value)}
    </td>
);

const SurfaceTable = ({ children }) => (
    <div className="pricing-showcase_surface-table overflow-x-auto rounded-[22px] border border-[#dbe2ea] bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
        {children}
    </div>
);

const MobilePairCard = ({ title, commonValue, fancyValue }) => (
    <div className="pricing-showcase_mobile-pair rounded-[16px] border border-[#eef2f6] bg-[#f8fafc] p-3">
        <div className="pricing-showcase_muted text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98a2b3]">
            {title}
        </div>
        {isSamePrice(commonValue, fancyValue) ? (
            <div className="pricing-showcase_mobile-value mt-3 rounded-[12px] bg-white px-3 py-2">
                <div className="pricing-showcase_muted text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98a2b3]">
                    Обычная / Видовая
                </div>
                <div className={`mt-1 text-[15px] font-semibold ${getCellTone(commonValue)}`}>
                    {formatPrice(commonValue)}
                </div>
            </div>
        ) : (
            <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="pricing-showcase_mobile-value rounded-[12px] bg-white px-3 py-2">
                    <div className="pricing-showcase_muted text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98a2b3]">
                        Обычная
                    </div>
                    <div className={`mt-1 text-[15px] font-semibold ${getCellTone(commonValue)}`}>
                        {formatPrice(commonValue)}
                    </div>
                </div>
                <div className="pricing-showcase_mobile-value rounded-[12px] bg-white px-3 py-2">
                    <div className="pricing-showcase_muted text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98a2b3]">
                        Видовая
                    </div>
                    <div className={`mt-1 text-[15px] font-semibold ${getCellTone(fancyValue)}`}>
                        {formatPrice(fancyValue)}
                    </div>
                </div>
            </div>
        )}
    </div>
);

const MobileRowCard = ({ floorRange, values }) => (
    <article className="pricing-showcase_mobile-row rounded-[20px] border border-[#dbe2ea] bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
        <div className="pricing-showcase_muted text-[12px] font-semibold uppercase tracking-[0.08em] text-[#98a2b3]">
            Этажи
        </div>
        <div className="pricing-showcase_primary mt-1 text-[18px] font-semibold text-[#101828]">{floorRange}</div>
        <div className="mt-4 grid gap-3">
            {values.map((item) => (
                <MobilePairCard
                    key={item.label}
                    title={item.label}
                    commonValue={item.commonValue}
                    fancyValue={item.fancyValue}
                />
            ))}
        </div>
    </article>
);

export const TableComponentModern = ({ data }) => {
    const [openPeriods, setOpenPeriods] = useState<Record<string, boolean>>({});
    const sortedFloors = useMemo(() => getSortedFloors(data), [data]);

    if (!data || sortedFloors.length === 0) {
        return null;
    }

    const isByFirstPayment =
        data.floorRanges &&
        data.floorRanges.length > 0 &&
        data.floorRanges[0].prices &&
        data.floorRanges[0].prices[0].hasOwnProperty("installmentPlan");

    if (!isByFirstPayment) {
        return (
            <>
                <div className="grid gap-3 md:hidden">
                    {sortedFloors.map((floor, index) => {
                        const price =
                            floor.prices?.find((p) => p.installmentPercent === "full") ||
                            floor.prices?.[0];

                        return (
                            <MobileRowCard
                                key={index}
                                floorRange={floor.floorRange}
                                values={[
                                    {
                                        label: "Стоимость",
                                        commonValue: price?.commonFlat,
                                        fancyValue: price?.fancyViewFlat,
                                    },
                                ]}
                            />
                        );
                    })}
                </div>

                <div className="hidden md:block">
                    <SurfaceTable>
                        <table className="min-w-full border-separate border-spacing-0">
                            <thead>
                            <tr className="pricing-showcase_table-head bg-[#f8fafc] text-[#667085]">
                                <th className="pricing-showcase_table-sticky sticky left-0 z-[2] border-b border-[#e4e7ec] bg-[#f8fafc] px-4 py-3 text-left text-[12px] font-semibold uppercase tracking-[0.08em]">
                                    Этажи
                                </th>
                                <th className="pricing-showcase_table-head-cell border-b border-[#e4e7ec] px-4 py-3 text-right text-[12px] font-semibold uppercase tracking-[0.08em]">
                                    Обычная
                                </th>
                                <th className="pricing-showcase_table-head-cell border-b border-[#e4e7ec] px-4 py-3 text-right text-[12px] font-semibold uppercase tracking-[0.08em]">
                                    Видовая
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedFloors.map((floor, index) => {
                                const price =
                                    floor.prices?.find((p) => p.installmentPercent === "full") ||
                                    floor.prices?.[0];
                                const rowBg = index % 2 === 0 ? "pricing-showcase_row-even" : "pricing-showcase_row-odd";

                                return (
                                    <tr key={index} className={rowBg}>
                                        <td className={`pricing-showcase_table-sticky pricing-showcase_primary sticky left-0 border-b border-[#eef2f6] px-4 py-3 text-[14px] font-semibold text-[#111827] ${rowBg}`}>
                                            {floor.floorRange}
                                        </td>
                                        <PriceCell value={price?.commonFlat} />
                                        <PriceCell value={price?.fancyViewFlat} />
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </SurfaceTable>
                </div>
            </>
        );
    }

    const periodsSet = new Set();
    sortedFloors.forEach((floor) => {
        floor.prices.forEach((price) => {
            if (price.installmentPeriod) {
                periodsSet.add(price.installmentPeriod);
            }
        });
    });
    const periods = Array.from(periodsSet).sort(
        (a, b) => parseInt(a, 10) - parseInt(b, 10)
    );

    const plansSet = new Set();
    sortedFloors.forEach((floor) => {
        floor.prices.forEach((price) => {
            plansSet.add(price.installmentPlan);
        });
    });
    const installmentPlans = Array.from(plansSet);
    const groupedPeriods = groupPeriodsBySignature(
        periods,
        sortedFloors,
        installmentPlans
    );

    const togglePeriod = (periodKey: string) => {
        setOpenPeriods((prev) => ({
            ...prev,
            [periodKey]: !(prev[periodKey] ?? false),
        }));
    };

    return (
        <div className="space-y-5">
            {groupedPeriods.map((periodGroup) => {
                const periodKey = periodGroup.periods.join("-");
                const representativePeriod = periodGroup.periods[0];
                const mergedPlans = installmentPlans.reduce((acc, plan) => {
                    const isMergedForAllFloors = sortedFloors.every((floor) => {
                        const price = floor.prices.find(
                            (item) =>
                                item.installmentPlan === plan &&
                                (item.installmentPeriod === representativePeriod ||
                                    item.installmentPeriod === "")
                        );

                        return isSamePrice(price?.commonFlat, price?.fancyViewFlat);
                    });

                    acc[plan] = isMergedForAllFloors;
                    return acc;
                }, {} as Record<string, boolean>);

                return (
                <section key={periodKey} className="space-y-3">
                    <button
                        type="button"
                        onClick={() => togglePeriod(periodKey)}
                        className="pricing-showcase_period-button flex w-full flex-col gap-2 rounded-[20px] border border-[#e4e7ec] bg-[linear-gradient(135deg,#fff7fb_0%,#f8fafc_100%)] px-4 py-3 text-left sm:pointer-events-none sm:flex-row sm:items-center sm:justify-between"
                    >
                        <div>
                            <div className="pricing-showcase_muted text-[12px] font-semibold uppercase tracking-[0.08em] text-[#7a8699]">
                                Период рассрочки
                            </div>
                            <div className="pricing-showcase_primary mt-1 text-[20px] font-semibold text-[#101828]">
                                {formatPeriodRange(periodGroup.periods)}
                            </div>
                        </div>
                        <div className="pricing-showcase_muted text-[13px] leading-5 text-[#667085]">
                            <span className="sm:hidden">
                                {(openPeriods[periodKey] ?? false) ? "Скрыть" : "Показать"}
                            </span>
                            <span className="hidden sm:inline">
                                Сравнение по этажам и размеру первого взноса.
                            </span>
                        </div>
                    </button>

                    <div
                        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out md:hidden ${
                            (openPeriods[periodKey] ?? false)
                                ? "grid-rows-[1fr] opacity-100"
                                : "grid-rows-[0fr] opacity-0"
                        }`}
                    >
                        <div className="overflow-hidden">
                            <div className="grid gap-3 pt-1">
                            {sortedFloors.map((floor, index) => {
                                const priceMap = {};
                                floor.prices
                                    .filter(
                                        (price) =>
                                            price.installmentPeriod === representativePeriod ||
                                            price.installmentPeriod === ""
                                    )
                                    .forEach((price) => {
                                        priceMap[price.installmentPlan] = price;
                                    });

                                return (
                                    <MobileRowCard
                                        key={index}
                                        floorRange={floor.floorRange}
                                        values={installmentPlans.map((plan) => {
                                            const price = priceMap[plan];
                                            const planLabel = INSTALLMENT_PLANS_LIB[plan]?.label || plan;
                                            return {
                                                label: planLabel,
                                                commonValue: price?.commonFlat,
                                                fancyValue: price?.fancyViewFlat,
                                            };
                                        })}
                                    />
                                );
                            })}
                        </div>
                        </div>
                    </div>

                    <div className="hidden md:block">
                        <SurfaceTable>
                            <table className="min-w-full border-separate border-spacing-0">
                                <thead>
                                <tr className="pricing-showcase_table-head bg-[#f8fafc] text-[#667085]">
                                    <th
                                        className="pricing-showcase_table-sticky sticky left-0 z-[3] border-b border-[#e4e7ec] bg-[#f8fafc] px-4 py-3 text-left text-[12px] font-semibold uppercase tracking-[0.08em]"
                                        rowSpan="2"
                                    >
                                        Этажи
                                    </th>
                                    {installmentPlans.map((plan) => (
                                        <th
                                            key={plan}
                                            colSpan={mergedPlans[plan] ? 1 : 2}
                                            className="pricing-showcase_table-head-cell border-b border-l border-[#e4e7ec] px-4 py-3 text-center text-[12px] font-semibold uppercase tracking-[0.08em]"
                                        >
                                            {INSTALLMENT_PLANS_LIB[plan]?.label || plan}
                                        </th>
                                    ))}
                                </tr>
                                <tr className="pricing-showcase_table-head bg-[#fcfcfd] text-[#98a2b3]">
                                    {installmentPlans.map((plan) =>
                                        mergedPlans[plan] ? (
                                            <th
                                                key={plan}
                                                className="pricing-showcase_table-head-cell border-b border-l border-[#e4e7ec] px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.08em]"
                                            >
                                                Обычная / Видовая
                                            </th>
                                        ) : (
                                            <React.Fragment key={plan}>
                                                <th className="pricing-showcase_table-head-cell border-b border-l border-[#e4e7ec] px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em]">
                                                    Обычная
                                                </th>
                                                <th className="pricing-showcase_table-head-cell border-b border-[#e4e7ec] px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em]">
                                                    Видовая
                                                </th>
                                            </React.Fragment>
                                        )
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {sortedFloors.map((floor, index) => {
                                    const rowBg = index % 2 === 0 ? "pricing-showcase_row-even" : "pricing-showcase_row-odd";
                                    const priceMap = {};
                                    floor.prices
                                        .filter(
                                            (price) =>
                                                price.installmentPeriod === representativePeriod ||
                                                price.installmentPeriod === ""
                                        )
                                        .forEach((price) => {
                                            priceMap[price.installmentPlan] = price;
                                        });

                                    return (
                                        <tr key={index} className={rowBg}>
                                            <td className={`sticky left-0 z-[2] border-b border-[#eef2f6] px-4 py-3 text-[14px] font-semibold text-[#111827] ${rowBg}`}>
                                                {floor.floorRange}
                                            </td>
                                            {installmentPlans.map((plan) => {
                                                const price = priceMap[plan];
                                                const merged = mergedPlans[plan];
                                                return (
                                                    <React.Fragment key={plan}>
                                                        {merged ? (
                                                            <td
                                                                className={`pricing-showcase_table-cell border-b border-l border-[#eef2f6] px-4 py-3 text-center text-[14px] font-semibold ${getCellTone(price?.commonFlat)}`}
                                                            >
                                                                {formatPrice(price?.commonFlat)}
                                                            </td>
                                                        ) : (
                                                            <>
                                                                <PriceCell
                                                                    value={price?.commonFlat}
                                                                    className="border-l"
                                                                />
                                                                <PriceCell value={price?.fancyViewFlat} />
                                                            </>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </SurfaceTable>
                    </div>
                </section>
                );
            })}
        </div>
    );
};
